/**
 * SS-3191: the new campaign report's detail page — one row per send (per person for a drip).
 * GET /reports/outreach/:id?rangeBeg=&rangeEnd=&q=[&format=csv|pdf]
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Link,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronLeft, faChevronRight, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { cqAPI, getBid } from "../../../lib/Auth";
import { FormatIcon } from "./OutreachReportList";
import {
  CHANNEL_LABELS,
  DATE_PRESETS,
  STATUS_LABELS,
  STEP_RESULT_LABELS,
  formatDateTime,
  formatDuration,
  goalLabel,
  nextSort,
  presetRange,
  rangeParams,
  searchPlaceholder,
  sortRows,
} from "./outreachReportUtils";

const DEFAULT_PRESET_DAYS = 28; // Last 4 weeks

const SortHeader = ({ label, sortKey, sort, onSort }) => (
  <Th cursor="pointer" onClick={() => onSort(sortKey)} whiteSpace="nowrap">
    {label}{" "}
    <FontAwesomeIcon icon={sort.key !== sortKey ? faSort : sort.direction === "ascending" ? faSortUp : faSortDown} />
  </Th>
);

const PatientLink = ({ row }) => {
  if (!row.patient_name) return <Text>—</Text>;
  if (!row.uid) return <Text>{row.patient_name}</Text>;
  return (
    <Link href={`/inbox/${row.uid}?bid=${row.bid || getBid()}`} color="blue.500">
      {row.patient_name}
    </Link>
  );
};

const GoalResult = ({ row }) => {
  if (row.rating !== undefined && row.rating !== null) return <Text>{row.rating}</Text>;
  if (!row.goal_result) return <Text color="gray.500">—</Text>;
  return <Badge colorScheme="green">{row.goal_result}</Badge>;
};

function SendTable({ rows, campaign, sort, setSort, navigate }) {
  const isCall = campaign.format === "phone" || campaign.format === "phone_ai";
  const isAi = campaign.format === "phone_ai";
  const isReview = campaign.purpose === "campaign-review";
  const onSort = (key) => setSort(nextSort(sort, key));
  return (
    <Table size="sm">
      <Thead>
        <Tr>
          <SortHeader label="Sent At" sortKey="sent_at" sort={sort} onSort={onSort} />
          <SortHeader label="Patient" sortKey="patient_name" sort={sort} onSort={onSort} />
          <SortHeader label={campaign.format === "email" ? "Email" : "Phone"} sortKey="contact" sort={sort} onSort={onSort} />
          <SortHeader label={isCall ? "Call Result" : "Status"} sortKey="status" sort={sort} onSort={onSort} />
          {isCall && <SortHeader label="Duration" sortKey="duration" sort={sort} onSort={onSort} />}
          {isAi && <Th>{"Ezra's Actions"}</Th>}
          <SortHeader label="Appointment" sortKey="appointment_at" sort={sort} onSort={onSort} />
          <SortHeader label={isReview ? "Rating" : "Goal"} sortKey={isReview ? "rating" : "goal_result"} sort={sort} onSort={onSort} />
          <SortHeader label="Goal Reached At" sortKey="goal_reached_at" sort={sort} onSort={onSort} />
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr
            key={row._id}
            data-testid="send-row"
            cursor={isAi && row.call_id ? "pointer" : undefined}
            onClick={isAi && row.call_id ? () => navigate(`/reports/calls/${row.call_id}?bid=${getBid()}`) : undefined}
          >
            <Td>{formatDateTime(row.sent_at)}</Td>
            <Td onClick={(event) => event.stopPropagation()}><PatientLink row={row} /></Td>
            <Td>{row.contact || "—"}</Td>
            <Td>{row.status}</Td>
            {isCall && <Td>{formatDuration(row.duration)}</Td>}
            {isAi && (
              <Td>
                {(row.ezra_actions || []).length ? (
                  <Flex gap={1} wrap="wrap">{row.ezra_actions.map((a) => <Badge key={a}>{a}</Badge>)}</Flex>
                ) : (
                  <Text color="gray.500">{row.ezra_engaged ? "No action" : "—"}</Text>
                )}
              </Td>
            )}
            <Td>{formatDateTime(row.appointment_at)}</Td>
            <Td><GoalResult row={row} /></Td>
            <Td>{formatDateTime(row.goal_reached_at)}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function DripTable({ rows, funnel, sort, setSort }) {
  const [open, setOpen] = useState({});
  const onSort = (key) => setSort(nextSort(sort, key));
  return (
    <>
      <Box mb={4} data-testid="drip-funnel">
        <Text fontWeight="bold" mb={2}>Step funnel</Text>
        <Table size="sm" maxW="720px">
          <Thead>
            <Tr><Th>Step</Th><Th isNumeric>Ran for</Th><Th isNumeric>Delivered / answered</Th><Th isNumeric>Goal reached after it</Th></Tr>
          </Thead>
          <Tbody>
            {(funnel || []).map((step) => (
              <Tr key={step.step}>
                <Td>{step.step + 1}. {CHANNEL_LABELS[step.channel] || step.channel}</Td>
                <Td isNumeric>{step.ran}</Td>
                <Td isNumeric>{step.reached}</Td>
                <Td isNumeric>{step.goal_reached_after}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th />
            <SortHeader label="Name" sortKey="patient_name" sort={sort} onSort={onSort} />
            <SortHeader label="Phone" sortKey="contact" sort={sort} onSort={onSort} />
            <SortHeader label="Enrolled At" sortKey="enrolled_at" sort={sort} onSort={onSort} />
            <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} />
            <SortHeader label="Steps Done" sortKey="steps_done" sort={sort} onSort={onSort} />
            <Th>Last Step</Th>
            <SortHeader label="Goal Reached At" sortKey="goal_reached_at" sort={sort} onSort={onSort} />
          </Tr>
        </Thead>
        <Tbody>
          {rows.flatMap((row) => {
            const isOpen = !!open[row._id];
            const main = (
              <Tr key={row._id} data-testid="person-row">
                <Td>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label={isOpen ? "Hide steps" : "Show steps"}
                    icon={<FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />}
                    onClick={() => setOpen({ ...open, [row._id]: !isOpen })}
                  />
                </Td>
                <Td><PatientLink row={row} /></Td>
                <Td>{row.contact || "—"}</Td>
                <Td>{formatDateTime(row.enrolled_at)}</Td>
                <Td>{STATUS_LABELS[row.status] || row.status}</Td>
                <Td>{row.steps_done} of {row.steps_total}</Td>
                <Td>
                  {row.last_step
                    ? `${Number(row.last_step.step) + 1}. ${CHANNEL_LABELS[row.last_step.channel] || row.last_step.channel || ""} — ${STEP_RESULT_LABELS[row.last_step.result] || row.last_step.result || "pending"}`
                    : "—"}
                </Td>
                <Td>{formatDateTime(row.goal_reached_at)}</Td>
              </Tr>
            );
            if (!isOpen) return [main];
            return [
              main,
              ...(row.steps_history || []).map((step, index) => (
                <Tr key={`${row._id}-${index}`} bg="gray.50" data-testid="step-row">
                  <Td />
                  <Td>Step {Number(step.step) + 1}</Td>
                  <Td>{CHANNEL_LABELS[step.channel] || step.channel}</Td>
                  <Td>Scheduled {formatDateTime(step.scheduled_at)}</Td>
                  <Td>{STEP_RESULT_LABELS[step.result] || step.result || "Pending"}</Td>
                  <Td>Executed {formatDateTime(step.executed_at)}</Td>
                  <Td colSpan={2}>
                    {step.call_id ? (
                      <Link href={`/reports/calls/${step.call_id}?bid=${getBid()}`} color="blue.500">Call details</Link>
                    ) : step.message_id && row.uid ? (
                      <Link href={`/inbox/${row.uid}?bid=${getBid()}`} color="blue.500">Message</Link>
                    ) : null}
                  </Td>
                </Tr>
              )),
            ];
          })}
        </Tbody>
      </Table>
    </>
  );
}

function OutreachReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [preset, setPreset] = useState(DEFAULT_PRESET_DAYS);
  const [range, setRange] = useState(() => presetRange(DEFAULT_PRESET_DAYS));
  const [query, setQuery] = useState("");
  const [applied, setApplied] = useState({ range: presetRange(DEFAULT_PRESET_DAYS), q: "" });
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sort, setSort] = useState({ key: "sent_at", direction: "descending", clicked: false });
  const location = useLocation();

  const params = useCallback(
    (extra = {}) => new URLSearchParams({ ...rangeParams(applied.range), ...(applied.q ? { q: applied.q } : {}), ...extra }).toString(),
    [applied],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const response = await cqAPI.get(`/reports/outreach/${encodeURIComponent(id)}?${params()}`);
        if (response?.type !== "ok") throw new Error(response?.msg || "Could not load the campaign.");
        if (!cancelled) {
          setData(response.data);
          setSort({ key: response.data?.drip ? "enrolled_at" : "sent_at", direction: "descending", clicked: false });
        }
      } catch (error) {
        if (!cancelled) {
          setData(null);
          toast({ status: "error", title: error.message });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, params, toast]);

  const tieKey = data?.drip ? "enrolled_at" : "sent_at";
  const rows = useMemo(() => sortRows(data?.rows || [], sort, tieKey), [data, sort, tieKey]);

  const download = async (format) => {
    const url = await cqAPI.downloadFile(`/reports/outreach/${encodeURIComponent(id)}?${params({ format })}`);
    if (typeof url !== "string") {
      toast({ status: "error", title: "The download failed." });
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = `${data?.campaign?.name || "campaign"}.${format}`;
    link.click();
  };

  const campaign = data?.campaign;
  const formatRow = campaign ? (data.drip ? { drip: { channels: campaign.channels, steps: (data.funnel || []).length } } : { format: campaign.format }) : null;

  return (
    <Box p={4}>
      <Button size="sm" variant="ghost" mb={2} leftIcon={<FontAwesomeIcon icon={faChevronLeft} />} onClick={() => {
        const bid = new URLSearchParams(location.search).get("bid");
        navigate(`/reports/campaigns?view=new${bid ? `&bid=${bid}` : ""}`);
      }}>
        Back to the campaign report
      </Button>
      {campaign && (
        <Flex align="center" gap={3} mb={4} wrap="wrap">
          <Text fontSize="xl" fontWeight="bold">{campaign.name}</Text>
          <FormatIcon row={formatRow} />
          <Text>Goal: {goalLabel(campaign.goal)}</Text>
          {campaign.goal?.estimated && (
            <Tooltip label="Estimated from the campaign's own text when past campaigns were backfilled">
              <Badge>estimated</Badge>
            </Tooltip>
          )}
        </Flex>
      )}
      <Flex gap={2} align="center" mb={4} wrap="wrap">
        <Select
          size="sm"
          w="160px"
          aria-label="Date preset"
          value={preset}
          onChange={(event) => {
            const days = Number(event.target.value);
            setPreset(days);
            if (days) setRange(presetRange(days));
          }}
        >
          {DATE_PRESETS.map((p) => (
            <option key={p.days} value={p.days}>{p.label}</option>
          ))}
          <option value={0}>Custom</option>
        </Select>
        <Box>
          <DatePicker
            selectsRange
            startDate={range[0]}
            endDate={range[1]}
            onChange={(next) => {
              setPreset(0);
              setRange(next);
            }}
            className="chakra-input css-0"
          />
        </Box>
        <Input
          size="sm"
          w="220px"
          placeholder={searchPlaceholder(campaign?.format)}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && setApplied({ range, q: query })}
        />
        <Button size="sm" colorScheme="blue" onClick={() => setApplied({ range, q: query })}>Show</Button>
        <Button size="sm" onClick={() => download("csv")} isDisabled={!data}>Download CSV</Button>
        <Button size="sm" onClick={() => download("pdf")} isDisabled={!data}>Download PDF</Button>
      </Flex>
      {isLoading ? (
        <Spinner />
      ) : !data ? (
        <Text color="gray.500">No data.</Text>
      ) : data.drip ? (
        <DripTable rows={rows} funnel={data.funnel} sort={sort} setSort={setSort} />
      ) : rows.length ? (
        <Box overflowX="auto">
          <SendTable rows={rows} campaign={campaign} sort={sort} setSort={setSort} navigate={navigate} />
        </Box>
      ) : (
        <Text color="gray.500">No sends in this range.</Text>
      )}
    </Box>
  );
}

export default OutreachReportDetail;
